import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { useChatStore } from '../../../shared/store/chatStore';
import { Logo } from '../../../shared/components/layout/Logo';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import { PageHeader } from '../../../shared/components/layout/pageHeader';
import { Header } from '../../../shared/components/layout/Header';
import * as PhosphorIcons from "@phosphor-icons/react";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';

interface ExplorationQuestion {
  id: string;
  question: string;
  prompt: string;
  image: string;
}

interface ExplorationTheme {
  id: string;
  title: string;
  description: string;
  icon: string;
  questions: ExplorationQuestion[];
}

export default function Discover() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { createChat, setInputValue } = useChatStore();
  const [themes, setThemes] = useState<ExplorationTheme[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchThemes = async () => {
      try {
        const apiUrl = import.meta.env.PROD 
          ? `${import.meta.env.VITE_API_URL}/exploration/themes`
          : '/api/exploration/themes';
          
        const response = await fetch(apiUrl);
        if (!response.ok) {
          throw new Error('Failed to fetch themes');
        }
        const data = await response.json();
        setThemes(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load themes');
      } finally {
        setLoading(false);
      }
    };

    fetchThemes();
  }, []);

  const handleQuestionClick = async (prompt: string) => {
    if (!user) return;
    
    sessionStorage.setItem('pendingMessage', prompt);
    
    const newChat = await createChat(prompt, user.id);
    if (newChat) {
      setInputValue(prompt);
      navigate(`/chat/${newChat.id}`);
    }
  };

  // Get the icon component dynamically
  const getIconComponent = (iconName: string) => {
    const Icon = (PhosphorIcons as any)[iconName];
    return Icon ? <Icon size={24} /> : null;
  };

  const renderQuestionCard = (question: ExplorationQuestion) => (
    <SwiperSlide key={question.id}>
      <button
        onClick={() => handleQuestionClick(question.prompt)}
        className="w-full bg-[#F5EEE8] rounded-xl hover:bg-[#EBE2DA] transition-colors text-left flex flex-col"
      >
        <div className="w-full aspect-square bg-[#E5DDD5] rounded-lg rounded-b-none overflow-hidden">
          <img 
            src={question.image}
            alt={question.question}
            className="w-full h-full object-cover"
          />
        </div>
        <h3 className="font-medium text-gray-900 p-3">
          {question.question}
        </h3>
      </button>
    </SwiperSlide>
  );

  const renderCarousel = (theme: ExplorationTheme) => (
    <div className="relative">
      <Swiper
        modules={[Navigation]}
        navigation={{
          prevEl: `#prev-${theme.id}`,
          nextEl: `#next-${theme.id}`,
        }}
        slidesPerView={3}
        slidesPerGroup={3}
        spaceBetween={20}
        className="!pl-0"
      >
        {theme.questions.map(renderQuestionCard)}
      </Swiper>
    </div>
  );

  if (loading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>;
  }

  if (error) {
    return <div className="flex justify-center items-center h-screen text-red-500">{error}</div>;
  }

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-3xl mx-auto px-4 py-12">
        <PageHeader
          headline="Explore the Bible"
          subheadline="Choose a topic to start your biblical exploration"
        />

        <div className="space-y-[70px] text-[#361E06] mt-[70px]">
          {themes.map((theme) => (
            <div key={theme.id} className="space-y-[30px]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="hidden p-2 bg-[#F5EEE8] rounded-lg">
                    {getIconComponent(theme.icon)}
                  </div>
                  <div>
                    <h2 className="text-3xl font-medium">{theme.title}</h2>
                    <p className="text-3xl font-light">{theme.description}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    id={`prev-${theme.id}`}
                    className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <CaretLeft className="w-5 h-5" />
                  </button>
                  <button
                    id={`next-${theme.id}`}
                    className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <CaretRight className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {renderCarousel(theme)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}